import React, { useEffect, useState } from 'react'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { apiGet } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { systemValueCodeLabel } from '../../generic/systemValues/systemValueLabel'
import { SmartSelect } from '../../generic/smartSelect'
import { useTheme } from '@emotion/react'
import { Fade } from '@mui/material'

// P19 (story 204): OTHER already has a key with another meaning (an alignment state), so this one reads by its own.
const CHANGE_LOG_TYPE_KEYS = { OTHER: 'CHANGE_LOG_TYPE_OTHER' }

const ProcessTraceability = () => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const [selectedType, setSelectedType] = useState(null)
  const [selectedWorkflow, setSelectedWorkflow] = useState(null)
  const [selectedPartReference, setSelectedPartReference] = useState(null)
  const [workflows, setWorkflows] = useState([])
  const [partReferences, setPartReferences] = useState([])
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false)
  const [isLoadingTable, setIsLoadingTable] = useState(false)
  const [tableKey, setTableKey] = useState(0)
  const [showTable, setShowTable] = useState(false)
  const typeSelectableOptions = [
    { code: 'TEMPLATE', name: 'Template' },
    { code: 'WORKFLOW', name: 'Workflow' }
  ]

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const references = await apiGet('partReferencesShortData')
        setPartReferences(references)
      } catch (err) {
        console.error('Error fetching part references:', err)
      }
    }
    fetchReferences()
  }, [])

  useEffect(() => {
    const fetchWorkflows = async () => {
      if (!selectedType) {
        setWorkflows([])
        return
      }

      try {
        setIsLoadingWorkflows(true)
        let workflowsData = []

        if (selectedType.name === 'Template') {
          workflowsData = await apiGet('getAllTemplateWorkflows')
          setWorkflows(workflowsData)
        } else if (selectedType.name === 'Workflow') {
          if (selectedPartReference) {
            workflowsData = await apiGet('workflowsByPartReferenceId/' + selectedPartReference.id)
          } else {
            workflowsData = await apiGet('getAllWorkflowsWithPartReference')
          }
          const workflowsWithDisplayNames = workflowsData.map(workflow => {
            const partRefName = workflow.partReferenceDto?.name || ''
            return {
              ...workflow,
              displayName: `${workflow.name} (${partRefName})`
            }
          })
          setWorkflows(workflowsWithDisplayNames)
        }
      } catch (err) {
        console.error('Error fetching workflows:', err)
      } finally {
        setIsLoadingWorkflows(false)
      }
    }
    fetchWorkflows()
  }, [selectedType, selectedPartReference])

  useEffect(() => {
    if (selectedWorkflow?.partReferenceDto) {
      setSelectedPartReference(selectedWorkflow.partReferenceDto)
    }
  }, [selectedWorkflow])

  useEffect(() => {
    if (selectedWorkflow) {
      setIsLoadingTable(true)
      setShowTable(false)

      const timer = setTimeout(() => {
        setTableKey(prev => prev + 1)
        setIsLoadingTable(false)
        setShowTable(true)
      }, 500)

      return () => clearTimeout(timer)
    } else {
      setShowTable(false)
      setIsLoadingTable(false)
    }
  }, [selectedWorkflow])

  const optionWorkflows = () => workflows?.map(opt => opt.displayName || opt.name)

  const optionPartreferences = () => partReferences?.map(opt => opt.name)

  const fetchFunction = async (pagination) => {
    if (!selectedWorkflow) return []
    try {
      const safePagination = {
        ...pagination,
        page: pagination.page ?? 0,
        size: pagination.size ?? 10
      }
      const processtraceability = await apiGet('processTraceability/' + selectedWorkflow.id, safePagination)
      return processtraceability
    } catch (err) {
      console.error('Error fetching process traceability:', err)
      return []
    }
  }

  const handleTypeChange = (type) => {
    if (!type) {
      setSelectedType(null)
      setSelectedWorkflow(null)
      setSelectedPartReference(null)
      setWorkflows([])
      return
    }
    const found = typeSelectableOptions.find(r => t_(r.name) === type || r.name === type)
    setSelectedType(found)
    setSelectedWorkflow(null)
    setSelectedPartReference(null)
  }

  const handleWorkflowChange = (displayName) => {
    if (!displayName) {
      setSelectedWorkflow(null)
      setSelectedPartReference(null)
      return
    }
    const found = workflows.find(wk => wk.displayName === displayName || wk.name === displayName)
    setSelectedWorkflow(found)
  }

  const handlePartReferenceChange = (refName) => {
    if (!refName) {
      setSelectedPartReference(null)
      setSelectedWorkflow(null)
      return
    }
    const found = partReferences.find(r => r.name === refName)
    setSelectedPartReference(found)
    setSelectedWorkflow(null)
  }
  function formatMsToHHMMSS (value) {
    if (value === null || value === undefined || value === '') return ''
    if (isNaN(value)) return value

    const totalSeconds = Math.floor(Number(value) / 1000)
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, '0')
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0')
    const seconds = String(totalSeconds % 60).padStart(2, '0')

    return `${hours}:${minutes}:${seconds}`
  }

  const columns = [
    { name: t_('Date'), field: 'date', type: 'dateTimePicker' },
    { name: t_('User'), field: 'userName' },
    { name: t_('Change'), field: 'change', render: (row) => systemValueCodeLabel(t_, 'ChangeLogType', row.change, CHANGE_LOG_TYPE_KEYS) },
    { name: t_('Field name'), field: 'fieldName' },
    {
      name: t_('Old Value'),
      field: 'oldValue',
      render: (row) => {
        return row.fieldName === 'theoreticalTime' || row.fieldName === 'preparationTimeFirstPart' || row.fieldName === 'preparationTimeNextParts'
          ? formatMsToHHMMSS(row.oldValue)
          : row.oldValue
      }
    },
    {
      name: t_('New Value'),
      field: 'newValue',
      render: (row) => {
        return row.fieldName === 'theoreticalTime' || row.fieldName === 'preparationTimeFirstPart' || row.fieldName === 'preparationTimeNextParts'
          ? formatMsToHHMMSS(row.newValue)
          : row.newValue
      }
    },
    { name: t_('Job'), field: 'jobName' },
    { name: t_('Task'), field: 'taskName' },
    { name: t_('Comment'), field: 'description' }
  ]

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        backgroundColor: theme.palette.background.color_373636
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: '1rem',
          marginTop: '1rem',
          marginLeft: '1rem'
        }}
      >
        <div style={{ width: '300px' }}>
          <SmartSelect
            label={t_('Type')}
            value={t_(selectedType?.name) ?? null}
            onChange={handleTypeChange}
            selectableOptions={typeSelectableOptions.map(option => t_(option.name))}
            disableClearable={false}
          />
        </div>

        {selectedType && (
          <div style={{ width: '300px' }}>
            <SmartSelect
              label={t_('Workflow')}
              value={selectedWorkflow?.displayName || selectedWorkflow?.name || null}
              onChange={handleWorkflowChange}
              selectableOptions={optionWorkflows()}
              disableClearable={false}
              autoSelectFirst={false}
              loading={isLoadingWorkflows}
            />
          </div>
        )}
        {selectedType?.name === 'Workflow' && (
          <div style={{ width: '300px' }}>
            <SmartSelect
              label={t_('Part Reference')}
              value={selectedPartReference?.name ?? null}
              onChange={handlePartReferenceChange}
              selectableOptions={
                selectedWorkflow?.partReferenceDto
                  ? [selectedWorkflow.partReferenceDto.name]
                  : optionPartreferences()
              }
              disableClearable={false}
              autoSelectFirst={false}
              disabled={!!selectedWorkflow?.partReferenceDto}
            />
          </div>
        )}
      </div>

      {isLoadingTable && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          gap: '0.75rem'
        }}
        >
          <div style={{
            width: '20px',
            height: '20px',
            border: `2px solid ${theme.palette.grey[300]}`,
            borderTop: `2px solid ${theme.palette.primary.main}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
          />
          <span style={{
            color: theme.palette.text.secondary,
            fontSize: '0.875rem'
          }}
          >
            {t_('Loading data') + '...'}
          </span>
        </div>
      )}
      <Fade in={showTable && !isLoadingTable} timeout={500}>
        <div>
          {selectedWorkflow && (
            <SmartMateriaUITable
              key={tableKey}
              columns={columns}
              title={t_('Process Traceability')}
              dataFetch={fetchFunction}
              pageable
              tableTuneUniqueKey='workflow_change_log'
            />
          )}
        </div>
      </Fade>
    </div>
  )
}

export default ProcessTraceability
